import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Rating from '@mui/material/Rating';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { inputBaseClasses } from '@mui/material/InputBase';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import FormControlLabel from '@mui/material/FormControlLabel';

import { fCurrencySymbol } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { ColorPicker } from 'src/components/color-utils';
import { NumberInput } from 'src/components/number-input';

// ----------------------------------------------------------------------

export function ProductFiltersDrawer({
  canReset,
  filters,
  options,
  maxValue,
  updateParams,
  onResetParams,
}) {
  const { t } = useTranslate();

  const openFilters = useBoolean();
  const showCategories = useBoolean();

  const { state: currentFilters, setState: updateFilters, resetState: resetFilters } = filters;

  // const [checked, setChecked] = useState(currentFilters.shipping);

  const handleResetFilters = () => {
    resetFilters();
    onResetParams();
  };

  // const handleFilterShipping = (newValue) => {
  //   setChecked(newValue);
  //   updateParams('shipping', newValue);
  //   updateFilters({ shipping: newValue });
  // };

  const handleFilterGender = (newValue) => {
    const _checked = currentFilters.gender.includes(newValue)
      ? currentFilters.gender.filter((value) => value !== newValue)
      : [...currentFilters.gender, newValue];
    updateFilters({ gender: _checked });
  };

  const handleFilterCategory = (newValue) => {
    updateParams('category', newValue === 'all' ? '' : newValue);
    updateFilters({ category: newValue });
  };

  const handleFilterColors = (newValue) => updateFilters({ colors: newValue });

  const handleFilterPriceRange = (newValue) => {
    updateParams('priceRange', newValue.priceRange.join('-'));
    updateFilters(newValue);
  };

  // const handleFilterPriceRange = (event, newValue) => updateFilters({ priceRange: newValue });

  const handleFilterRating = (newValue) => updateFilters({ rating: newValue });

  const renderHead = () => (
    <>
      <Box sx={{ py: 2, pr: 1, pl: 2.5, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('Filters')}
        </Typography>

        <Tooltip title={t('Reset')}>
          <IconButton onClick={handleResetFilters}>
            <Badge color="error" variant="dot" invisible={!canReset}>
              <Iconify icon="solar:restart-bold" />
            </Badge>
          </IconButton>
        </Tooltip>

        <IconButton onClick={openFilters.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />
    </>
  );

  // const renderShipping = () => (
  //   <Stack direction="row" alignItems="center" justifyContent="space-between">
  //     <Typography variant="subtitle2" sx={{ mb: 1 }}>
  //       {t('Free shipping')}
  //     </Typography>

  //     <Switch
  //       checked={currentFilters.shipping || checked}
  //       onChange={(event, value) => handleFilterShipping(value)}
  //       slotProps={{ input: { 'aria-label': 'controlled' }}}
  //     />
  //   </Stack>
  // );

  const renderCategory = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="subtitle2"
        onClick={showCategories.onToggle}
        sx={{ mb: 1, display: 'flex', cursor: 'pointer', justifyContent: 'space-between' }}
      >
        {t('Category')}
        {showCategories.value ? <ExpandLess /> : <ExpandMore />}
      </Typography>

      <Collapse in={showCategories.value} timeout="auto" unmountOnExit>
        {options.categories.map((option) => (
          <FormControlLabel
            key={option}
            label={t(option)}
            control={
              <Radio
                checked={option === currentFilters.category}
                onClick={() => handleFilterCategory(option)}
                slotProps={{ input: { id: `${option}-radio` } }}
              />
            }
            sx={{ width: 1, ...(option === 'all' && { textTransform: 'capitalize' }) }}
          />
        ))}
      </Collapse>
    </Box>
  );

  const renderGender = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('Gender')}
      </Typography>

      <Box gap={1} display="grid" gridTemplateColumns="repeat(3, 1fr)">
        {options.genders.map((option) => (
          <FormControlLabel
            key={option.value}
            label={t(option.label)}
            control={
              <Checkbox
                checked={currentFilters.gender.includes(option.label)}
                onClick={() => handleFilterGender(option.label)}
                slotProps={{ input: { id: `${option.value}-checkbox` } }}
              />
            }
          />
        ))}
      </Box>
    </Box>
  );

  const renderColor = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('Color')}
      </Typography>
      <ColorPicker
        options={options.colors}
        value={currentFilters.colors}
        onChange={(colors) => handleFilterColors(colors)}
        limit={6}
      />
    </Box>
  );

  const renderPrice = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2">{t('Price')}</Typography>

      <Box gap={2} display="flex" sx={{ my: 2 }}>
        <InputRange
          type="min"
          value={currentFilters.priceRange}
          onChange={handleFilterPriceRange}
          max={maxValue}
        />
        <InputRange
          type="max"
          value={currentFilters.priceRange}
          onChange={handleFilterPriceRange}
          max={maxValue}
        />
      </Box>
    </Box>
  );

  const renderRating = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('Rating')}
      </Typography>

      {options.ratings.map((item, index) => (
        <Box
          key={item}
          onClick={() => handleFilterRating(item)}
          sx={{
            mb: 1,
            gap: 1,
            ml: -1,
            p: 0.5,
            display: 'flex',
            borderRadius: 1,
            cursor: 'pointer',
            typography: 'body2',
            alignItems: 'center',
            '&:hover': { opacity: 0.48 },
            ...(currentFilters.rating === item && { bgcolor: 'action.selected' }),
          }}
        >
          <Rating readOnly value={4 - index} /> {`+${4 - index}`}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        onClick={openFilters.onTrue}
        endIcon={
          <Badge color="error" variant="dot" invisible={!canReset}>
            <Iconify icon="ic:round-filter-list" />
          </Badge>
        }
        sx={{ display: { xs: 'none', md: 'inline-flex' } }}
      >
        {t('Filters')}
      </Button>

      <IconButton
        onClick={openFilters.onTrue}
        color="inherit"
        sx={{ mr: -1.5, display: { md: 'none' } }}
      >
        <Badge color="error" variant="dot" invisible={!canReset}>
          <Iconify icon="ic:round-filter-list" />
        </Badge>
      </IconButton>

      <SwipeableDrawer
        anchor="right"
        disableDiscovery
        disableSwipeToOpen
        open={openFilters.value}
        disableBackdropTransition
        onOpen={openFilters.onTrue}
        onClose={openFilters.onFalse}
        slotProps={{ paper: { sx: { width: { xs: 1, md: 320 } } } }}
      >
        {renderHead()}

        <Scrollbar sx={{ px: 2.5, py: 3 }}>
          <Stack spacing={3}>
            {/* {renderShipping()} */}
            {renderCategory()}
            {renderGender()}
            {renderColor()}
            {renderPrice()}
            {renderRating()}
          </Stack>
        </Scrollbar>
      </SwipeableDrawer>
    </>
  );
}

function InputRange({ max, type, value, onChange: onFilters }) {
  const minValue = value[0];
  const maxValue = value[1];

  const { currentLang } = useTranslate();

  const handleBlur = () => {
    const newMin = Math.max(0, Math.min(minValue, max));
    const newMax = Math.max(0, Math.min(maxValue, max));

    if (newMin !== minValue || newMax !== maxValue) {
      onFilters({ priceRange: [newMin, newMax] });
    }
  };

  return (
    <Stack
      spacing={1}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: 1 }}
    >
      <Typography
        variant="caption"
        sx={{
          flexGrow: 1,
          color: 'text.disabled',
          textTransform: 'capitalize',
          fontWeight: 'fontWeightSemiBold',
        }}
      >
        {`${type} ${fCurrencySymbol(currentLang.numberFormat)}`}
      </Typography>

      <NumberInput
        max={max}
        hideButtons
        value={type === 'min' ? minValue : maxValue}
        onChange={(event, newValue) =>
          onFilters({ priceRange: type === 'min' ? [newValue, maxValue] : [minValue, newValue] })
        }
        onBlur={handleBlur}
        sx={{ maxWidth: 64 }}
        slotProps={{
          input: { sx: { [`& .${inputBaseClasses.input}`]: { pr: 1, textAlign: 'right' } } },
        }}
      />
    </Stack>
  );
}
